import {
  WSActions,
  WSControl,
  WSElement,
  WSForm,
  WSList,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import {
  GetMissingFieldsLevelParameter,
  VerificationLevel
} from "@wingspanhq/wingspan-api-sdk";
import { complianceEntitiesApi, onboardingApi } from "@api";
import { queriesComplianceEntities, queriesOnboarding } from "@query";
import { WSServiceError } from "../../../../../utils/serviceHelper";
import { ErrorContextKey } from "../../../../../services/platform";
import { useWSMutation, WSQueryCache } from "@ws-react-query";
import { useFlowContext } from "../../../../../shared/components/Flow";
import { getUsPersonFormEmptyValues } from "../../../components/FormPartialUsPersonInfo/emptyValues";
import { FormPartialUsPersonInfo } from "../../../components/FormPartialUsPersonInfo";
import { validationSchemaUsPersonInfo } from "../../../components/FormPartialUsPersonInfo/validationSchema";
import { FlowUsPersonInfoContext, FormDataUsPersonInfo } from "../getSteps";
import { useUserId } from "../../../../../query/hooks/helpers";
import { useQueryUserProfile } from "../../../../../query/users/queries";
import { useCallback, useMemo, useState } from "react";
import { verificationService } from "../../../../../shared/services/verificaiton";
import { useUsPersonMissingFields } from "../../../hooks/useUsPersonMissingFields";
import { PATH_SETTINGS_ACCOUNT_PERSONAL } from "../../../../Settings/paths";
import { useHistory } from "react-router-dom";
import { useModalAbandon } from "../useModalAbandon";
import { getVerificationState } from "../../../utils/getVerificationState";
import {
  QUERY_USERS_USER_PROFILE_V2,
  useQueryUserProfileV2
} from "../../../../../query/user/queries/useQueryUserProfile";
import merge from "lodash/merge";
import { selectorUsPersonPartialFormData } from "../../../selectors/selectorUsPersonPartialFormData";
import { FlowStep } from "../../../../../shared/components/FlowSwitch/types";

export const StepUsPersonInfoForm: React.FC<FlowStep> = props => {
  const [context, setContext] = useFlowContext<FlowUsPersonInfoContext>();
  const [submitUsPersonInfo, submitMeta] = useWSMutation<
    void,
    WSServiceError,
    FormDataUsPersonInfo
  >(
    async data => {
      // The US-person step targets the caller's own primary compliance entity
      // (subject == null, not end-dated).
      const entities = await complianceEntitiesApi.listComplianceEntities({});
      const primary = (entities.data ?? []).find(
        entity => !entity.subject && !entity.endDate
      );

      if (!primary) {
        throw new Error("No compliance entity found");
      }

      // Tax identifiers are add-only: re-submitting a registration that already
      // exists is a material change (409), so only add a US SSN that isn't on
      // the entity yet. It rides alongside the individual legal name (not a
      // jurisdiction change, so a single PATCH is allowed).
      const existingTaxIdKeys = new Set(
        (primary.taxIdentifiers ?? [])
          .filter(taxId => taxId.hasValue)
          .map(taxId => `${taxId.countryCode}:${taxId.type}`)
      );
      const ssn = data.usPersonInfo.ssn;
      const taxIdentifiers =
        ssn && !existingTaxIdKeys.has("US:SSN")
          ? [{ countryCode: "US", type: "SSN", value: ssn }]
          : undefined;

      await complianceEntitiesApi.updateComplianceEntity({
        complianceEntityId: primary.id,
        complianceEntityUpdate: {
          individualLegalName: {
            givenName: data.usPersonInfo.firstName || undefined,
            middleNames: data.usPersonInfo.middleName || undefined,
            familyName: data.usPersonInfo.lastName || undefined
          },
          taxIdentifiers
        }
      });

      await onboardingApi.triggerVerification({
        verifyRequest: { level: GetMissingFieldsLevelParameter.TAX }
      });
    },
    {
      dependencies: [
        queriesComplianceEntities.listComplianceEntities.queryKeyBase,
        queriesOnboarding.getOnboardingStatus.queryKeyBase,
        queriesOnboarding.getMissingFields.queryKeyBase
      ]
    }
  );
  const history = useHistory();
  const modalAbandon = useModalAbandon();

  const userId = useUserId();
  const queryUser = useQueryUserProfile(userId);
  const queryUserProfile = useQueryUserProfileV2();

  const [loading, setLoading] = useState(false);

  const verificationLevel = GetMissingFieldsLevelParameter.TAX;

  const usPersonMissingFields = useUsPersonMissingFields(verificationLevel);

  const isUsPersonMismatch = useMemo(
    () => verificationService.getIsUsPersonMismatch(usPersonMissingFields),
    [usPersonMissingFields]
  );

  const goBack = useCallback(() => {
    history.push(PATH_SETTINGS_ACCOUNT_PERSONAL);
  }, [history]);

  return (
    <WSPanel shimmer={queryUser.isLoading || queryUserProfile.isLoading}>
      <WSForm<FormDataUsPersonInfo>
        defaultValues={
          context.usPersonInfo ?? {
            isUsPerson: false,
            usPersonInfo: merge(
              getUsPersonFormEmptyValues(),
              selectorUsPersonPartialFormData({
                dob: queryUser.data?.profile.dob,
                userProfile: queryUserProfile.data
              })
            )
          }
        }
        validationSchema={Yup.object().shape({
          isUsPerson: Yup.boolean(),
          usPersonInfo: Yup.mixed().when("isUsPerson", {
            is: true,
            then: validationSchemaUsPersonInfo
          })
        })}
        onSubmit={async data => {
          setContext(prev => ({ ...prev, usPersonInfo: data }));

          if (!data.isUsPerson) {
            goBack();
            return;
          }

          setLoading(true);

          await submitUsPersonInfo(data, {
            async onSuccess() {
              await WSQueryCache.invalidateQueries(QUERY_USERS_USER_PROFILE_V2);

              const { usPersonStatus, missingData } =
                await getVerificationState(verificationLevel);

              const shouldStop =
                usPersonStatus === VerificationLevel.UPDATE_REQUIRED &&
                missingData.profiles?.["US"]?.requiredFields &&
                missingData.profiles?.["US"]?.requiredFields?.length > 0;

              setLoading(false);

              if (!shouldStop) props.onNext?.();
            },
            onError() {
              setLoading(false);
            }
          });
        }}
      >
        <WSList gap="2XL">
          <WSElement>
            <WSSectionToolbar title="Update taxpayer information" />
            <WSText.ParagraphSm mt="L" color="gray500">
              Please review to ensure the information is accurate before
              submitting
            </WSText.ParagraphSm>
          </WSElement>

          <WSElement>
            <WSText.ParagraphSm weight="medium">
              Add U.S. person information
            </WSText.ParagraphSm>
            <WSText.ParagraphSm mt="XS" color="gray500">
              Only check the box to confirm you&apos;re a U.S. citizen, green
              card holder, or U.S. tax resident—even if you live in another
              country.
            </WSText.ParagraphSm>
            <WSForm.Field
              mt="L"
              name="isUsPerson"
              component={WSControl}
              componentProps={{
                type: "checkbox",
                size: "S",
                label: "Yes, I am a U.S. person"
              }}
            />
          </WSElement>

          <WSForm.Value name="isUsPerson">
            {(isUsPerson: boolean) =>
              isUsPerson ? (
                <FormPartialUsPersonInfo
                  isEdit={true}
                  mismatch={isUsPersonMismatch}
                  name="usPersonInfo"
                />
              ) : null
            }
          </WSForm.Value>

          <WSErrorMessage
            contextKey={ErrorContextKey.MonitoredEntity}
            error={submitMeta.error}
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                label: "Cancel",
                onClick: () => {
                  modalAbandon.open({
                    onContinue: goBack
                  });
                },
                kind: "Secondary",
                type: "button"
              },
              {
                label: "Continue",
                loading: submitMeta.isLoading || loading
              }
            ]}
          />

          <WSText.ParagraphXs color="gray400">
            The information on this page is required to request a taxpayer
            information update, if you exit or leave without completing this
            step, it will cancel the request.
          </WSText.ParagraphXs>
        </WSList>
      </WSForm>
    </WSPanel>
  );
};
